import React, { useMemo } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { useManagementCompanyLogoBlobUrl } from '../../wrh/reports/useManagementCompanyLogo';
import type { OnboardStatusResponse } from '../types';
import { OnboardStatusReport } from './OnboardStatusReport';

function buildBackPath(params: URLSearchParams): string {
  const vesselId = params.get('vesselId')?.trim() ?? '';
  return vesselId ? `/crew/onboard-status` : '/crew/onboard-status';
}

const OnboardStatusReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const rankId = searchParams.get('rankId')?.trim() ?? '';
  const query = searchParams.get('q')?.trim() ?? '';

  const listUrl = vesselId
    ? `/crew/reports/onboard-status?vesselId=${encodeURIComponent(vesselId)}`
    : null;

  const { data, error, isLoading } = useSWR<OnboardStatusResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const rows = useMemo(() => {
    let list = data?.rows ?? [];
    if (rankId) {
      const rid = Number(rankId);
      list = list.filter((row) => row.rankId === rid);
    }
    const q = query.toLowerCase();
    if (!q) return list;
    return list.filter((row) => {
      const haystack = [
        row.name,
        row.crewRef,
        row.rank,
        row.nationality,
        row.aiOverview?.headline,
        row.aiOverview?.readiness,
        ...(row.aiOverview?.strengths ?? []),
        ...(row.aiOverview?.risks ?? []),
        ...(row.aiOverview?.recommendations ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data?.rows, query, rankId]);

  const managementCompany = useMemo(() => {
    const manager = data?.vessel?.manager;
    if (manager?.id) {
      return {
        id: manager.id,
        name: manager.name,
        logoStorageKey: manager.logoStorageKey ?? null,
      };
    }
    return null;
  }, [data?.vessel?.manager]);

  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    managementCompany?.id,
    managementCompany?.logoStorageKey,
  );

  const backTo = buildBackPath(searchParams);

  if (!vesselId) {
    return <Navigate to="/crew/onboard-status" replace />;
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading Onboard Status…
      </div>
    );
  }

  if (error || !data?.vessel) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load onboard status.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Onboard Status
        </Link>
      </div>
    );
  }

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Onboard Status"
      title="Onboard Status"
      subtitle={`${data.vessel.vesselName} · ${rows.length} crew`}
      printPageSize="A4 portrait"
      printMargin="0"
    >
      <OnboardStatusReport
        data={data}
        rows={rows}
        companyLogoSrc={companyLogoSrc}
        companyName={managementCompany?.name}
      />
    </ReportView>
  );
};

export default OnboardStatusReportPage;
